<template>
  <div class="p-4 max-w-screen-sm mx-auto text-sm">

    <!-- FORM INVOICE -->
    <div class="invoice-form-container">

      <!-- Nama Billing -->
      <div class="form-group">
        <label class="form-label">Nama Billing</label>
        <input
          class="form-input"
          v-model="billingName"
          @input="onBillingInput"
          placeholder="Nama customer"
        />
        <ul v-if="customerSuggestions.length" class="suggestion-list">
          <li
            v-for="c in customerSuggestions"
            :key="c.id"
            @click="selectCustomer(c)"
          >
            <strong>{{ c.billing_name }}</strong><br />
            <small>{{ c.address }}</small>
          </li>
        </ul>
      </div>

      <!-- Nama & HP -->
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">No. HP Billing</label>
          <input v-model="billingPhone" placeholder="08xxxxxxx" />
        </div>

        <div class="form-group">
          <label class="form-label">Nama Penerima (opsional)</label>
          <input v-model="receiverName" />
        </div>

        <div class="form-group">
          <label class="form-label">No. HP Penerima (opsional)</label>
          <input v-model="receiverPhone" />
        </div>
      </div>

      <!-- Alamat -->
      <div class="form-group">
        <label class="form-label">Alamat</label>
        <textarea v-model="address"></textarea>
      </div>

      <!-- Pengiriman -->
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Delivery By</label>
          <select v-model="deliveryMethodId">
            <option
              v-for="d in deliveryOptions"
              :key="d.id"
              :value="d.id"
            >
              {{ d.name }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Pool</label>
          <select v-model="pollId">
            <option
              v-for="p in pollOptions"
              :key="p.id"
              :value="p.id"
            >
              {{ p.name }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Tanggal Pengiriman</label>
          <input type="date" v-model="deliveryDate" />
        </div>
        <div class="form-group">
          <label class="form-label">PO Number (Optional)</label>
          <input v-model="poNumber" placeholder="Masukkan PO Number jika ada" />
        </div>
      </div>
      <!-- ITEMS -->
      <div
        v-for="(item, i) in items"
        :key="i"
        class="product-row"
      >
        <Multiselect
          v-model="item.selected"
          :options="productOptions"
          labelProp="label"
          valueProp="value"
          :object="true"
          searchable
          placeholder="Pilih produk"
          @update:modelValue="opt => onSelectProduct(item, opt)"
        />

        <input
          type="number"
          v-model.number="item.quantity"
          min="1"
          :readonly="isSpecialItem(item)"
          :class="{ locked: isSpecialItem(item) }"
        />

        <input
          type="number"
          v-model.number="item.price"
          @input="fixSpecialPrice(item)"
          :readonly="!isEditablePrice(item)"
          :class="{ locked: !isEditablePrice(item) }"
        />

        <button class="btn-delete" @click="removeItem(i)">Hapus</button>
      </div>

      <button class="btn-add" @click="addItem">+ Produk</button>
    </div>

    <!-- ================= INVOICE PREVIEW (A4) ================= -->
    <div class="invoice-frame-wrapper mt-10">
      <div class="invoice-wrapper"  ref="invoicePreview">

        <!-- ================= HEADER ================= -->
        <div class="invoice-header-grid">

          <!-- ===== ROW 1 ===== -->
          <div class="header-row top">
            <!-- LEFT : LOGO -->
            <div class="logo-box">
              <img src="../assets/img/logo/Logo.png" class="logo" />
            </div>

            <!-- RIGHT : COMPANY INFO -->
            <div class="company-info">
              <strong>Imah Kopi Croissanterie (CV Kaysen Ekaboga Makmur)</strong><br />
              Jl. Maribaya No.3, Lembang, Bandung 40391<br />
              hello@imahkopi.com<br />
              www.imahkopi.com
            </div>
          </div>

          <!-- ===== ROW 2 ===== -->
          <div class="header-row bottom">
            <!-- LEFT : OUTLET + CUSTOMER -->
            <div class="customer-info">
              <div><strong>Outlet Name:</strong></div>
              <div><span class="big-text">{{ billingName || '-' }}</span></div>
              <div><strong>Address:</strong> {{ address || '-' }}</div>
              <div><strong>Phone Number:</strong> {{ billingPhone || '-' }}</div>
              <div><strong>Customer Name:</strong> {{ receiverName || '-' }}</div>
            </div>

            <!-- RIGHT : INVOICE META -->
            <div class="invoice-meta">
              <div class="meta-row">
                <span>Invoice No</span>
                <strong>{{ invoiceNumber }}</strong>
              </div>
              <div class="meta-row" v-if="poNumber">
                <span>PO Number</span>
                <strong>{{ poNumber }}</strong>
              </div>
              <div class="meta-row">
                <span>Invoice Date</span>
                <strong>{{ formatDateID(invoiceDate) }}</strong>
              </div>
              <div class="meta-row">
                <span>Due Date</span>
                <strong>{{ formatDateID(dueDate) }}</strong>
              </div>

              <!-- TOTAL BESAR -->
              <div class="grand-total">
                <span>Rp</span>
                <strong>{{ format(grandTotal) }}</strong>
              </div>
            </div>
          </div>

        </div>


        <!-- BODY -->
        <div class="invoice-body">
          <!-- TABLE -->
          <table class="invoice-table">
            <thead>
              <tr>
                <th style="width:40px;">NO</th>
                <th>ITEM</th>
                <th class="text-right" style="width:60px;">QTY</th>
                <th class="text-right" style="width:120px;">UNIT PRICE</th>
                <th class="text-right" style="width:120px;">TOTAL</th>
              </tr>
            </thead>
            <!-- ITEMS -->
            <tbody>
              <tr v-for="(it, i) in productItems" :key="i">
                <td>{{ i + 1 }}</td>
                <td>{{ it.name }}</td>
                <td class="text-right">{{ it.quantity }}</td>
                <td class="text-right">Rp{{ format(it.price) }}</td>
                <td class="text-right">
                  Rp{{ format(it.price * it.quantity) }}
                </td>
              </tr>
            </tbody>


            <!-- SUB TOTAL -->
            <tfoot>

              <!-- Sub Total -->
              <tr class="tfoot-row subtotal">
                <td colspan="4" class="fw-bold">Sub Total<small class="qty-info"> (Total Item: {{ totalQty }})</small></td>
                <td class="text-end fw-bold">Rp{{ format(subTotal) }}</td>
              </tr>
            </tfoot>

          </table>


        <!-- ===== SUMMARY OUTSIDE TABLE ===== -->
          <div class="invoice-summary">

            <!-- DISKON INVOICE -->
            <div v-if="diskonInvoiceValue" class="summary-row">
              <span>Diskon Invoice</span>
              <span>-Rp{{ format(Math.abs(diskonInvoiceValue)) }}</span>
            </div>

            <!-- DISKON CASHBACK -->
            <div v-if="diskonValue" class="summary-row">
              <span>Lebih Deposit + Cashback</span>
              <span>-Rp{{ format(Math.abs(diskonValue)) }}</span>
            </div>
            <!-- KURANG BAYAR -->
            <div v-if="kurangBayarValue" class="summary-row">
              <span>Kurang Bayar Invoice Sebelumnya</span>
              <span>Rp{{ format(kurangBayarValue) }}</span>
            </div>
            <!-- ONGKOS -->
            <div v-if="ongkirValue" class="summary-row">
              <span>Deposit Ongkos Kirim</span>
              <span>Rp{{ format(ongkirValue) }}</span>
            </div>

            <hr />

            <!-- GRAND TOTAL -->
            <div class="summary-row summary-total">
              <span class="label">TOTAL</span>
              <span class="value">Rp{{ format(grandTotal) }}</span>
            </div>

          </div>


        </div>

       <!-- FOOTER PAYMENT BOX -->
        <div class="invoice-footer">
          <div class="payment-box">
            <div class="pay-title">PAYMENT METHOD</div>

            <div class="pay-row">
              <span>Bank:</span>
              <strong>BCA</strong>
            </div>

            <div class="pay-row">
              <span>Branch:</span>
              <strong>KCP Lembang</strong>
            </div>

            <div class="pay-row">
              <span>Account Name:</span>
              <strong>CV. Kaysen Ekaboga Makmur</strong>
            </div>

            <div class="pay-row pay-number">
              <span>Account Number:</span>
              <strong>137.88888.23</strong>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- SAVE BUTTON -->

    <button class="save-btn" @click="saveInvoice">
      Simpan Invoice
    </button>

  </div>
</template>

<script>
import Multiselect from "@vueform/multiselect";
import Swal from "sweetalert2";
import html2canvas from "html2canvas";

export default {
  name: "InvoicePage",
  components: { Multiselect },

  data() {
    const today = new Date().toISOString().slice(0,10);
    return {
        invoiceNumber: "",
        invoiceDate: today,
        dueDate: today,
        poNumber: "",
        selectedCustomer: null,

        deliveryMethodId: null,
        pollId: null,

        deliveryOptions: [],
        pollOptions: [],

        deliveryDate: today,

        isEdit: false,
        invoiceId: null,

        billingName: "",
        billingPhone: "",
        receiverName: "",
        receiverPhone: "",
        address: "",

        customerSuggestions: [],
        products: [],
        items: [{ selected: null, name: "", quantity: 1, price: 0 }],
      };
    },

  computed: {
    // =============================
    // DISKON INVOICE (KHUSUS)
    // =============================
    diskonInvoiceValue() {
      const d = this.items.find(i =>
        (i.name || i.selected?.value || "")
          .toLowerCase()
          .includes("diskon invoice")
      );

      return d
        ? (Number(d.price) || 0) * (Number(d.quantity) || 1)
        : 0;
    },
    // =============================
    // PRODUCT OPTIONS (MULTISELECT)
    // =============================
    productOptions() {
      return this.products.map(p => ({
        value: p.name,
        label: p.name,
        price: Number(p.price) || 0,
        id: p.id,          // 🔥 tambah ini
      }));
    },

    // =============================
    // ITEM PRODUK SAJA (UNTUK TABLE)
    // =============================
    productItems() {
      const grouped = {};

      this.items.forEach(item => {
        const name = (item.name || item.selected?.value || "").trim();

        if (!name) return;

        const lower = name.toLowerCase();

        // skip diskon & ongkos
        if (
          lower.includes("diskon") ||
          lower.includes("ongkos") ||
          lower.includes("kurang bayar invoice")
        ) return;

        if (!grouped[name]) {
          grouped[name] = {
            name,
            quantity: 0,
            price: Number(item.price) || 0
          };
        }

        grouped[name].quantity += Number(item.quantity) || 0;
      });

      return Object.values(grouped);
    },


    // =============================
    // TOTAL QTY (PRODUK SAJA)
    // =============================
    totalQty() {
      return this.productItems.reduce(
        (t, i) => t + (Number(i.quantity) || 0),
        0
      );
    },

    // =============================
    // SUB TOTAL (PRODUK SAJA)
    // =============================
    subTotal() {
      return this.productItems.reduce(
        (t, i) =>
          t +
          (Number(i.price) || 0) *
          (Number(i.quantity) || 0),
        0
      );
    },

    // =============================
    // DISKON BIASA (CASHBACK)
    // =============================
    diskonValue() {
      const d = this.items.find(i => {
        const name = (i.name || i.selected?.value || "").toLowerCase();

        return (
          name.includes("diskon") &&
          !name.includes("diskon invoice")
        );
      });

      return d
        ? (Number(d.price) || 0) * (Number(d.quantity) || 1)
        : 0;
    },
    // =============================
    // KURANG BAYAR INVOICE
    // =============================
    kurangBayarValue() {
      const k = this.items.find(i =>
        (i.name || i.selected?.value || "")
          .toLowerCase()
          .includes("kurang bayar invoice")
      );

      return k
        ? (Number(k.price) || 0) * (Number(k.quantity) || 1)
        : 0;
    },
    // =============================
    // ONGKOS KIRIM
    // =============================
    ongkirValue() {
      const o = this.items.find(i =>
        (i.name || i.selected?.value || "")
          .toLowerCase()
          .includes("ongkos")
      );

      return o
        ? (Number(o.price) || 0) * (Number(o.quantity) || 1)
        : 0;
    },

    // =============================
    // GRAND TOTAL (FINAL)
    // =============================
    grandTotal() {
      return (
        this.subTotal +
        this.diskonInvoiceValue +
        this.diskonValue +
        this.kurangBayarValue + // 🔥 TAMBAH INI
        this.ongkirValue
      );
    }
  },
  watch: {
  items: {
    deep: true,
    handler(items) {
      items.forEach(item => {
        if (!item.selected) return;

        // ⚠️ HANYA SET NAMA
        // ❌ JANGAN SENTUH PRICE DI SINI
        item.name = item.selected.value;
      });
    }
  }
},

async mounted() {
  // LOAD PRODUCTS
  const res = await fetch("/api/get_products.php");
  const json = await res.json();
  this.products = json.data || [];

  // 🔥 LOAD DELIVERY METHODS
  const dRes = await fetch("/api/delivery_methods/get_delivery_methods.php");
  const dJson = await dRes.json();
  this.deliveryOptions = dJson.data || [];

  // default pilih pertama
  if (this.deliveryOptions.length) {
    this.deliveryMethodId = this.deliveryOptions[0].id;
  }

  // 🔥 LOAD POLLS
  const pRes = await fetch("/api/polls/get_polls.php");
  const pJson = await pRes.json();
  this.pollOptions = pJson.data || [];

  if (this.pollOptions.length) {
    this.pollId = this.pollOptions[0].id;
  }

  // ===== EDIT / CREATE MODE =====
  const id = this.$route.query.id;

  if (id) {
    this.isEdit = true;
    this.invoiceId = Number(id);
    await this.loadInvoice(id);
  } else {
    await this.reserveNewInvoiceNumber();
  }
},
  methods: {
  isBottomChargeItem(name) {
    const n = (name || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

    return (
      n.includes("charge paperbag") ||
      n.includes("charge paperbox") ||
      n.includes("charge packaging") ||
      n.includes("wrapping")
    );
  },

  isSpecialItem(item) {
    if (!item.name) return false;

    const label = item.name.toLowerCase();

    return (
      label.includes("diskon") ||
      label.includes("ongkos") ||
      label.includes("kurang bayar invoice")
    );
  },

  isEditablePrice(item) {
    if (!item.name) return false;

    const label = item.name.toLowerCase();

    return (
      label.includes("ongkos") ||
      label.includes("diskon") ||
      label.includes("kurang bayar invoice")
    );
  },

   fixSpecialPrice(item) {
      if (!item.name) return;

      if (item.name.toLowerCase().includes("diskon")) {
        // ⛔ JANGAN PAKSA DEFAULT
        // ✅ HANYA BALIKIN KE NEGATIF
        item.price = -Math.abs(item.price || 0);
      }
    },

    async reserveInvoiceNumber() {
      const res = await fetch("/api/reserve_invoice_number.php");
      const json = await res.json();

      if (!json.success) {
        Swal.fire("Error", "Gagal generate invoice number", "error");
        return;
      }

      this.invoiceNumber = json.invoice_number;
    },
        async fetchInvoiceNumber() {
      const res = await fetch("/api/reserve_invoice_number.php");
      const json = await res.json();

      if (!json.success) {
        throw new Error("Gagal generate invoice number");
      }

      this.invoiceNumber = json.invoice_number;
    },
    formatMinus(val) {
      const n = Math.abs(Number(val || 0));
      return `-Rp${n.toLocaleString("id-ID")}`;
    },
    async loadInvoice(id) {
      try {
        const res = await fetch(`/api/get_invoice_detail.php?id=${id}`);
        const json = await res.json();

        if (!json.success) {
          throw new Error(json.message || "Gagal load invoice");
        }

        const inv = json.data.invoice;
        const items = json.data.items;

        // ===============================
        // INVOICE META
        // ===============================
        this.invoiceNumber = inv.invoice_number ?? this.invoiceNumber;

        // ===============================
        // CUSTOMER INFO
        // ===============================
        this.billingName   = inv.billing_name ?? "";
        this.billingPhone  = inv.billing_phone ?? "";
        this.receiverName  = inv.receiver_name ?? "";
        this.poNumber  = inv.po_number ?? "";
        this.receiverPhone = inv.receiver_phone ?? "";
        this.address       = inv.address ?? "";

        // ===============================
        // DELIVERY + POLL (FOREIGN KEY)
        // ===============================
        this.deliveryDate =
          inv.delivery_date && inv.delivery_date !== "0000-00-00"
            ? inv.delivery_date
            : new Date().toISOString().slice(0,10);


        this.deliveryMethodId =
          inv.delivery_method_id !== null
            ? Number(inv.delivery_method_id)
            : null;

        this.pollId =
          inv.poll_id !== null
            ? Number(inv.poll_id)
            : null;

        // ===============================
        // ITEMS
        // ===============================
        this.items = items.map(it => {
          const opt = this.productOptions.find(
            p => p.value === it.product_name
          );

          return {
            selected: opt || {
              value: it.product_name,
              label: it.product_name,
              price: it.price
            },
            name: it.product_name,
            product_id: opt ? opt.id : (it.product_id || null),   // 🔥 tambah ini
            quantity: Number(it.quantity),
            price: Number(it.price)
          };
        });

      } catch (e) {
        console.error(e);
        Swal.fire("Error", e.message, "error");
      }
    },

    formatDateID(date) {
      const d = new Date(date);
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
      }).replace(',', '');
    },
    // ================= UTIL =================
    format(n) {
      return Number(n || 0).toLocaleString("id-ID");
    },

    // ================= CUSTOMER =================
    async onBillingInput() {
    this.selectedCustomer = null;

    if (this.billingName.length < 2) {
      this.customerSuggestions = [];
      return;
    }

    const keyword = this.billingName.trim();

    const res = await fetch(
      `/api/get_customers.php?q=${encodeURIComponent(keyword)}`
    );

    this.customerSuggestions = await res.json();
  },
    selectCustomer(c) {
      this.selectedCustomer = c;

      // ===============================
      // DATA CUSTOMER
      // ===============================
      this.billingName = c.billing_name;
      this.billingPhone = c.billing_phone;

      this.receiverName = c.receiver_name || c.billing_name;
      this.receiverPhone = c.receiver_phone || c.billing_phone;

      this.address = c.address;

      // ===============================
      // ✅ AUTO FILL DELIVERY + POLL (DEFAULT)
      // ===============================
      if (c.delivery_method_id) {
        this.deliveryMethodId = Number(c.delivery_method_id);
      }

      if (c.poll_id) {
        this.pollId = Number(c.poll_id);
      }

      // ===============================
      // CLEAR SUGGESTIONS
      // ===============================
      this.customerSuggestions = [];
    },


    // ================= ITEMS =================
    addItem() {
        this.items.push({
          selected: null,
          name: "",
          quantity: 1,
          price: 0,
        });
    },


    removeItem(i) {
      this.items.splice(i, 1);
    },

    onSelectProduct(item, opt) {
      if (!opt) {
        item.selected = null;
        item.name = "";
        item.product_id = null;   // 🔥
        item.price = 0;
        item.quantity = 1;
        return;
      }

      item.selected = opt;
      item.name = opt.value;
      item.product_id = opt.id; 
      const label = item.name.toLowerCase();
      const defaultPrice = Number(opt.price) || 0;

      // SPECIAL ITEM → qty selalu 1
      if (label.includes("diskon") || label.includes("ongkos")) {
        item.quantity = 1;
      }

      // DISKON
      if (label.includes("diskon")) {
        item.price = 0;
        return;
      }

      // ONGKOS
      if (label.includes("ongkos")) {
        item.price = defaultPrice;
        return;
      }

      // PRODUK NORMAL
      item.price = defaultPrice;
    },

    // ================= RESERVE INVOICE NUMBER =================

      async reserveNewInvoiceNumber() {
        const res = await fetch("/api/reserve_invoice_number.php");
        const json = await res.json();

        if (!json.success) {
          Swal.fire("Gagal generate invoice number");
          return;
        }

        this.invoiceNumber = json.invoice_number;
      },
      
    // ================= SAVE INVOICE =================
    async saveInvoice() {

  // ================= VALIDASI =================
  if (!this.billingName) {
    Swal.fire("Nama billing wajib diisi");
    return;
  }

  const validItems = this.items.filter(i => i.name);

  if (!validItems.length) {
    Swal.fire("Minimal 1 produk");
    return;
  }

  // ================= MERGE PRODUK DUPLIKAT =================
  const mergedItems = {};

  validItems.forEach(i => {
    const name = i.name.trim();

    // skip diskon / ongkos jangan digabung
    const lower = name.toLowerCase();
    if (lower.includes("diskon") || lower.includes("ongkos") || lower.includes("kurang bayar invoice")) {
      mergedItems[name + Math.random()] = {
        name,
        quantity: Number(i.quantity),
        price: Number(i.price)
      };
      return;
    }

    if (!mergedItems[name]) {
      mergedItems[name] = {
        name,
        product_id: i.product_id || null, 
        quantity: 0,
        price: Number(i.price)
      };
    }

    mergedItems[name].quantity += Number(i.quantity);
  });

  // ================= PAYLOAD =================
  const payload = {
    invoice_date: this.invoiceDate,
    delivery_date: this.deliveryDate,

    delivery_method_id: this.deliveryMethodId,
    poll_id: this.pollId,

    po_number: this.poNumber?.trim() || null,

    items: Object.values(mergedItems)
  };

  // ================= MODE CREATE / EDIT =================
  if (!this.isEdit) {
    payload.invoice_number = this.invoiceNumber;
    payload.status = "Belum Dibayar";
  } else {
    payload.invoice_id = this.invoiceId;
  }

  // ================= CUSTOMER =================
  if (this.selectedCustomer) {
    payload.customer_id = this.selectedCustomer.id;
  } else {
    payload.billing_name = this.billingName;
    payload.billing_phone = this.billingPhone;
    payload.receiver_name = this.receiverName;
    payload.receiver_phone = this.receiverPhone;
    payload.address = this.address;
  }
  
    const user = JSON.parse(localStorage.getItem("user"));
    payload.created_by = user?.id || null;  

  // ================= URL =================
  let url = "/api/save_invoice.php";

  if (this.isEdit) {
    url = "/api/edit_invoice.php";
  }

  // ================= REQUEST =================
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (!data.success && data.status !== "success") {
      Swal.fire("Gagal", data.message || "Terjadi kesalahan", "error");
      return;
    }

    await this.downloadInvoiceJPG();

    Swal.fire({
      icon: "success",
      title: this.isEdit ? "Invoice diperbarui" : "Invoice tersimpan",
      timer: 1200,
      showConfirmButton: false,
    });

    if (this.isEdit) {
      setTimeout(() => {
        this.$router.push({ name: "Transaksi" });
      }, 1200);
      return;
    }

    await this.reserveNewInvoiceNumber();
    this.resetForm(false);

  } catch (err) {
    console.error(err);
    Swal.fire("Error", "Server error", "error");
  }
},

    async downloadInvoiceJPG() {
      const target = this.$refs.invoicePreview;
      if (!target) return;

      const canvas = await html2canvas(target, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        scrollY: 0,
        width: target.offsetWidth + 20,
        height: target.offsetHeight + 20,
        x: -10,
        y: -10,
      });

      // ✅ Outlet name aman buat filename
      const outlet = (this.billingName || "Outlet")
        .replace(/[^a-z0-9\s-]/gi, "")
        .trim()
        .replace(/\s+/g, " ");

      // ✅ Invoice number aman (hapus slash)
      const safeInvoice = String(this.invoiceNumber || "Invoice")
        .replace(/[/\\]/g, "_")   // ✅ FIX ESLINT DISINI
        .replace(/\s+/g, "");

      const link = document.createElement("a");

      // 🔥 Format wajib: OutletName-NoInvoice.jpg
      link.download = `${outlet}-${safeInvoice}.jpg`;

      link.href = canvas.toDataURL("image/jpeg", 1.0);
      link.click();
    },
   resetForm(resetInvoiceNumber = true) {
      const today = new Date().toISOString().slice(0, 10);

      if (resetInvoiceNumber) {
        this.invoiceNumber = "";
      }

      this.selectedCustomer = null;
      this.billingName = "";
      this.billingPhone = "";
      this.receiverName = "";
      this.receiverPhone = "";
      this.address = "";
      this.deliveryMethodId = this.deliveryOptions[0]?.id || null;
      this.pollId = this.pollOptions[0]?.id || null;
      this.deliveryDate = today;

      this.items = [
        { selected: null, name: "", quantity: 1, price: 0 },
      ];
    }
  },
};
</script>


<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@200..900&display=swap');
/* ============================= */
/* FORM CONTAINER */
/* ============================= */
.invoice-form-container {
  background: #f8f9fc;
  padding: 28px;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,.04);
  font-family: "Outfit", sans-serif;
}

/* ============================= */
/* LABEL */
/* ============================= */
.form-label {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  color: #111;
}

/* ============================= */
/* INPUT / SELECT / TEXTAREA */
/* ============================= */
input[type="text"],
input[type="number"],
input[type="date"],
select,
textarea {
  width: 100%;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  color: #111;
  transition: border-color .2s ease, box-shadow .2s ease;
}

/* focus */
input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,.15);
}

/* textarea */
textarea {
  min-height: 90px;
  resize: vertical;
}

/* ============================= */
/* ROW & GROUP */
/* ============================= */
.form-group {
  margin-bottom: 16px;
}

.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.form-row > .form-group {
  flex: 1;
  min-width: 200px;
}

/* ============================= */
/* PRODUCT ROW */
/* ============================= */
.product-row {
  display: flex;
  gap: 12px;
  margin-top: 12px;
  align-items: center;
}

.product-row input {
  max-width: 100px;
}

/* ============================= */
/* MULTISELECT */
/* ============================= */
.multiselect {
  border-radius: 10px !important;
  border-color: #d1d5db !important;
  font-size: 14px;
}

.multiselect:hover,
.multiselect:focus-within {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59,130,246,.15);
}

/* ============================= */
/* RESPONSIVE */
/* ============================= */
@media (max-width: 640px) {
  .product-row {
    flex-direction: column;
    align-items: stretch;
  }
}

/* ================= A4 CANVAS ================= */
.invoice-frame-wrapper {
  background: #fff;
  padding: 40px 0;
  display: flex;
  justify-content: center;
}

/* ================= HEADER ================= */
.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  max-width: 200px;
}

.invoice-info {
  text-align: right;
  font-size: 13px;
  line-height: 1.4;
}
.btn-add {
  margin-top: 16px;
  background: #3b82f6;
  color: #fff;
  padding: 10px 18px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  border: none;
  transition: background .2s ease, transform .15s ease;
}

.btn-add:hover {
  background: #2563eb;
  transform: translateY(-1px);
}
/* ================= BILL / TITLE ================= */
.invoice-bill-logo {
  margin-top: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.label {
  font-size: 12px;
  font-weight: 600;
}

.customer-name {
  font-size: 14px;
  font-weight: 600;
  margin-top: 4px;
}

.invoice-title {
  font-size: 36px;
  font-weight: 500;
  letter-spacing: 1px;
}

/* ================= TABLE ================= */
.invoice-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 32px;
}

.invoice-table thead tr {
  border-top: 2px solid #000;
  border-bottom: 2px solid #000;
}

.invoice-table th {
  padding: 10px 6px;
  text-align: left;
  font-weight: 600;
}

.invoice-table td {
  padding: 10px 6px;
  vertical-align: top;
}

/* ================= TOTAL ================= */
.invoice-total-row {
  margin-top: 24px;
  padding-top: 12px;
  border-top: 2px solid #000;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  font-size: 15px;
  font-weight: 600;
}

/* ================= FOOTER ================= */
.invoice-footer {
  margin-top: 80px;
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}

.payment {
  text-align: right;
}
.save-btn {
  margin-top: 32px;
  background-color: #10b981;
  color: white;
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 16px;
  border: none;
  cursor: pointer;
  transition: background 0.3s;
}
.invoice-header-grid {
  margin-bottom: 30px;
}

/* ===== ROW ===== */
.header-row {
  display: flex;
  justify-content: space-between;
  gap: 40px;
}

.header-row.top {
  align-items: flex-start;
  margin-bottom: 20px;
}

.header-row.bottom {
  align-items: flex-start;
}

/* ===== LOGO ===== */
.logo-box {
  width: 40%;
}

.logo {
  max-width: 100px;
}

/* ===== COMPANY INFO ===== */
.company-info {
  width: 60%;
  text-align: right;
  font-size: 13px;
  line-height: 1.4;
}

/* ===== CUSTOMER ===== */
.customer-info {
  width: 55%;
  font-size: 13px;
  line-height: 1.6;
}

/* ===== INVOICE META ===== */
.invoice-meta {
  width: 45%;
  text-align: right;
  font-size: 13px;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  gap: 16px;
}

/* ===== GRAND TOTAL ===== */
.grand-total {
  margin-top: 14px;
  background: #e5e5e5;
  padding: 10px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 22px;
  font-weight: 700;
}

.grand-total span {
  font-size: 18px;
}
.btn-delete {
  background: transparent;
  border: 1px solid #ef4444;
  color: #ef4444;
  padding: 8px 14px;
  border-radius: 10px;
  font-weight: 500;
  font-size: 13px;
  transition: all .2s ease;
}

.btn-delete:hover {
  background: #ef4444;
  color: #fff;
}
.save-btn {
  margin-top: 28px;
  width: 100%;
  background: #111827;
  color: #fff;
  padding: 14px 0;
  border-radius: 14px;
  font-weight: 700;
  font-size: 15px;
  border: none;
  transition: background .2s ease, transform .15s ease;
}

.save-btn:hover {
  background: #000;
  transform: translateY(-1px);
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;               /* jarak label ↔ input */
  margin-bottom: 16px;
}
.form-label {
  display: block;
  font-weight: 600;
  font-size: 13px;
  color: #111;
  line-height: 1.3;
}
.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
}
.form-group {
  position: relative;
}
.suggestion-list {
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  margin-top: 6px;
}
.suggestion-list li {
  padding: 10px 12px;
  cursor: pointer;
  transition: background .2s ease;
}
.form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.form-row .form-group {
  flex: 1;
  min-width: 220px;
}
@media (max-width: 640px) {
  .form-row {
    flex-direction: column;
  }
}
.form-input,
.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  color: #111;
  transition: border-color .2s ease, box-shadow .2s ease;
}

/* Focus state */
.form-input:focus,
.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,.15);
}
.invoice-summary {
  margin-top: 14px;
  margin-left: auto;
  width: 100%;
  max-width: 100%;
}

.invoice-summary .summary-row {
  display: grid;
  grid-template-columns: 1fr 120px; /* 🔥 SAMA DENGAN KOLOM TOTAL */
  align-items: center;
  margin-bottom: 6px;
}

.invoice-summary .summary-row span:last-child {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.summary-divider {
  border-top: 2px solid #000;
  margin: 12px 0;
}

.invoice-wrapper {
  width: 794px;
  min-height: 1123px;      /* A4 HEIGHT */
  padding: 48px 56px;
  background: #fff;
  color: #111;
  font-family: "Outfit", sans-serif;
  font-size: 13px;
  border: 1px solid #000;

  /* 🔥 INI KUNCI */
  display: flex;
  flex-direction: column;
}
.invoice-footer {
  margin-top: auto;       /* 🔥 PUSH KE BAWAH */
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  padding-top: 24px;
}
.invoice-body {
  margin-top: 24px;
}
.invoice-table tfoot tr {
  border-top: 2px solid #000;
}

.subtotal-row td {
  font-weight: 700;
  padding-top: 12px;
}
.invoice-summary {
  margin-top: 14px;
  max-width: 360px;
  margin-left: auto;
  font-size: 13px;
}

.invoice-summary .row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}
.invoice-summary {
  margin-top: 12px;
  width: 360px;          /* sejajar TOTAL */
  margin-left: auto;    /* dorong ke kanan */
  font-size: 13px;
}

.invoice-summary {
  margin-top: 14px;
  width: 360px;        /* samakan dengan kolom TOTAL tabel */
  margin-left: auto;
  font-size: 13px;
}

/* SEMUA ROW SUMMARY */
.summary-row {
  display: grid;
  grid-template-columns: 1fr 120px; /* 🔥 120px = TOTAL column */
  align-items: center;
  margin-bottom: 6px;
}

/* LABEL KIRI */
.summary-row .label {
  text-align: left;
}

/* VALUE KANAN (ANGKA) */
.summary-row .value {
  text-align: right;
  font-variant-numeric: tabular-nums;
}


.invoice-table tfoot .tfoot-row td {
  padding: 10px 6px;
}

.invoice-table tfoot .subtotal td {
  font-size: 14px;
}

.invoice-table tfoot .grand td {
  font-size: 40px;
  font-weight: 700;
}

.invoice-table tfoot .tfoot-sep td {
  padding: 8px 0;
  border-top: 2px solid #000;
}
.text-right {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.invoice-table th.text-right,
.invoice-table td.text-right {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
/* WRAPPER SUMMARY */
.invoice-summary {
  margin-top: 14px;
  width: 360px;          /* FIX – sejajar kolom TOTAL tabel */
  margin-left: auto;
  font-size: 13px;
}

/* ROW DEFAULT */
.summary-row {
  display: grid;
  grid-template-columns: 1fr 120px; /* 🔥 120px = kolom TOTAL */
  align-items: center;
  margin-bottom: 6px;
}

/* LABEL KIRI */
.summary-row .label {
  text-align: left;
}

/* VALUE KANAN */
.summary-row .value {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;  /* 🔥 CEGAH KELUAR CONTAINER */
}

/* ===== TOTAL FINAL ===== */
.summary-total {
  margin-top: 12px;
  padding-top: 12px;
  /* border-top: 2px solid #000; */
}

.summary-total .label,
.summary-total .value {
  font-size: 20px;
  font-weight: 700;
}
.invoice-footer {
  margin-top: 70px;
  display: flex;
  justify-content: center;
}

/* PAYMENT BOX */
.payment-box {
  border: 2px solid #000;
  padding: 18px 26px;
  border-radius: 14px;
  text-align: center;
  width: 100%;
  max-width: 420px;
}

/* TITLE */
.pay-title {
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 14px;
  text-transform: uppercase;
}

/* ROWS */
.pay-row {
  font-size: 14px;
  margin-bottom: 6px;
  display: flex;
  justify-content: space-between;
}

/* ACCOUNT NUMBER HIGHLIGHT */
.pay-number {
  margin-top: 12px;
  font-size: 18px;
  font-weight: 900;
}
.invoice-footer{
  margin-top: auto;        /* 🔥 ini kuncinya */
  display: flex;
  justify-content: center;
  padding-top: 18px;
}
.invoice-table tbody td:first-child {
  border-right: 2px solid #000;
}

.invoice-table thead th:first-child {
  border-right: 2px solid #000;
}
.locked {
  background: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}
.big-text{
  font-size: 30px !important;
  margin-top: -15px;
}
</style>
